import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { Product, ApplicantProduct } from '@workflow/ecp-workflow/dynamic-app/types/accounts-and-services.type';
import { StatefulService } from '@app/service/stateful.service';

/** Share Contribution Source */
const SHARE_CONTRIBUTION_SOURCE = 'ShareContributionSource';
/** Shares & Services Picklist Path */
const SHARE_SERVICES_PICKLIST_PATH = 'ss:oao-accounts-and-services-picklist';

/** Card for retirement account */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ecp-oao-retirement-account',
  styleUrls: ['./oao-retirement-account.component.scss'],
  templateUrl: './oao-retirement-account.component.html'
})
export class OaoRetirementAccountComponent implements OnInit {
  private readonly statefulService = inject(StatefulService);

  /** input for retirement saving account card */
  @Input()
  public retirementAccount: Product[];

  /** all account products */
  @Input() public allProducts: Product[];

  /** Callback Parent method to enable the continue button if all validations are valid */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() public enableContinueButton = new EventEmitter<any>();

  /** Shared Form */
  /** DEV NOTE: Angular 8 -
   * either need to set static to true to keep 'sharedCertForm' in onInit
   * or need to move 'sharedCertForm' reference to ngAfterViewInit
   */
  @ViewChild('retireAccountForm', { static: true }) public retireAccountForm: NgForm;

  /** co Applicant Products */
  @Input() public coApplicantProducts: ApplicantProduct[];

  /** Is Initial Loading from L360 Call */
  @Input() public isInitialLoading: boolean;

  /** Co Applicants Share : Local Variable */
  public coAppProducts: ApplicantProduct[];

  /** Storage ID */
  public arrayStorageId = 'api:oaoMemberInformation/accountsAndServices/primaryApplicant/shares';

  /** form subscription */
  public frmSub: Subscription;

  /** current year */
  public currentYear = new Date().getFullYear();

  /** contribution dropdown label and values */
  public contributionOptions = [];

  /** flag used for accordion */
  public expand = false;

  constructor(
    public readonly applicationApiService: ApplicationApiService,
    public readonly l360Service: L360Service,
    private readonly bundleHelperService: BundleHelperService
  ) {}

  public ngOnInit(): void {
    this.LoadPicklistData();
    this.coAppProducts = this.coApplicantProducts.map((x) => Object.assign({}, x));
    /** Subscribe ngForm status change event */
    // retireAccountForm MAY NEED TO BE MOVED TO ngAfterViewInit AFTER ANGULAR 8 UPGRADE //
    this.frmSub = this.retireAccountForm.statusChanges.subscribe((x) => {
      if (this.checkValidSelection()) {
        this.enableContinueButton.next(
          x.toString().toLowerCase() === 'invalid' ? { page: 'retirement', valid: false } : { page: 'retirement', valid: true }
        );
      } else {
        this.enableContinueButton.next({ page: 'retirement', valid: false });
      }
    });
    this.ValidatePage();
  }
  public isTaxValid(account: Product): boolean {
    return account.isSelected && account.taxYear === '';
  }
  public isContributionSourceValid(account: Product): boolean {
    return account.isSelected && account.contributionSource === '';
  }
  public LoadPicklistData(): void {
    const picklist = this.statefulService.wrap(SHARE_SERVICES_PICKLIST_PATH).valueString;
    if (picklist) {
      const picklistTypes = JSON.parse(picklist);

      // Check Share Contribution Source
      const shareContribution = picklistTypes.filter((t) => t.value.toLowerCase() === SHARE_CONTRIBUTION_SOURCE.toLowerCase());
      if (shareContribution && shareContribution.length === 1) {
        this.contributionOptions = shareContribution[0].pickListItems;
      }
    }
  }

  /** To get option for tax dropdown  */
  public taxOptions = [
    { label: `Previous Tax Year - ${this.currentYear - 1}`, value: `${this.currentYear - 1}` },
    { label: `Current Tax Year - ${this.currentYear}`, value: `${this.currentYear}` }
  ];

  /** returns Title */
  public get title(): string {
    return this.retirementAccount.length > 0 ? this.retirementAccount[0].typeName : '';
  }
  /** primary applicant's Share Selection checkbox */
  public primaryShareSelection(account: Product): void {
    // If Primary share is deselected then clear its co applicant's share & its value
    if (!account.isSelected) {
      account.taxYear = '';
      account.contributionSource = '';

      this.coAppProducts.map((applicant) => {
        applicant.shares
          .filter((f) => f.id === account.id)
          .map((share) => {
            share.isSelected = false;
            share.isDisabled = false;
            share.taxYear = '';
            share.contributionSource = '';
          });
      });
    } else {
      if (this.isYouthMembership()) {
        // If youth membership, then whatever share selected by Primary Applicant applicable for all co applicant's
        this.coAppProducts.map((applicant) => {
          applicant.shares
            .filter((f) => f.id === account.id)
            .map((share) => {
              share.isSelected = true;
              share.isDisabled = true;
            });
        });
      }
    }

    this.UpdateBundleIndex();
    this.updateSelection();
  }
  // On Selection of Co Applicant's Share checkbox
  public coAppShareSelection(parentAccount: Product, coApplicantProduct: ApplicantProduct): void {
    const shareId = parentAccount.id;
    const isSelected = coApplicantProduct.shares.filter((f) => f.id === shareId)[0].isSelected;
    const rawReqBody = [];

    // Set Co Applicant Share Selection Status
    rawReqBody.push(this.generateRequestBody(coApplicantProduct.bundlePath, parentAccount.bundleIndex, 'isSelected', isSelected));

    // Check whether max allowed applicant selection met or not, if so, then disable other shares
    const maxAllowedApplicant = parentAccount.numberOfJointApplicants;
    let selectedCoApp = 0;

    // Count Total Selected co Applicant Share
    this.coAppProducts.map((applicant) => {
      applicant.shares
        .filter((f) => f.id === shareId)
        .map((item) => {
          selectedCoApp = item.isSelected ? selectedCoApp + 1 : selectedCoApp;
        });
    });

    // If Max Allowed Co App selection equals to selected co app shares then disable remaing co applicant
    if (selectedCoApp === maxAllowedApplicant) {
      this.coAppProducts.map((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .map((share) => {
            share.isDisabled = share.isSelected ? false : true;
            share.taxYear = share.isDisabled ? '' : parentAccount.taxYear;
            share.contributionSource = share.isDisabled ? '' : parentAccount.contributionSource;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled));
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'taxYear', share.taxYear));
            rawReqBody.push(
              this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'contributionSource', share.contributionSource)
            );
          });
      });
    } else {
      this.coAppProducts.map((applicant) => {
        applicant.shares
          .filter((f) => f.id === shareId)
          .map((share) => {
            share.isDisabled = false;
            share.taxYear = parentAccount.taxYear;
            share.contributionSource = parentAccount.contributionSource;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled));
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'taxYear', share.taxYear));
            rawReqBody.push(
              this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'contributionSource', share.contributionSource)
            );
          });
      });
    }

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {})
        .catch(() => {});
    }, 0);
    this.updateSelection();
  }

  /** Update Dropdown Selection */
  public updateDropdownSelection(account: Product): void {
    const rawReqBody = [];
    rawReqBody.push(this.generateRequestBody('primaryApplicant', account.bundleIndex, 'contributionSource', account.contributionSource));
    rawReqBody.push(this.generateRequestBody('primaryApplicant', account.bundleIndex, 'taxYear', account.taxYear));
    this.coAppProducts.map((applicant) => {
      applicant.shares
        .filter((f) => f.id === account.id)
        .map((share) => {
          if (share.isSelected) {
            share.contributionSource = account.contributionSource;
            share.taxYear = account.taxYear;
            rawReqBody.push(this.generateRequestBody(applicant.bundlePath, share.bundleIndex, 'isDisabled', share.isDisabled));
          }
        });
    });

    setTimeout(() => {
      firstValueFrom(this.applicationApiService.bundle(rawReqBody))
        .then(() => {
          // Form Validation
          const isValid = this.checkValidSelection();
          if (isValid) {
            this.enableContinueButton.next({ page: 'retirement', valid: true });
          } else {
            this.enableContinueButton.next({ page: 'retirement', valid: false });
          }
        })
        .catch(() => {});
      return;
    }, 0);

    this.updateSelection();
  }

  /** Generate Request Body */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public generateRequestBody(shareBundlePath: string, shareBundleIndex: number, propName: string, value: any): any {
    return {
      op: 'add',
      path: `oaoMemberInformation/accountsAndServices/${shareBundlePath}/shares/${shareBundleIndex}/${propName}`,
      value
    };
  }

  // Update isSelected property of Account/Optionsl Servies
  public UpdateBundleIndex(): void {
    this.l360Service.UpdateBundleIndex(this.allProducts).subscribe(() => {
      this.bundleHelperService.refreshBundle();
    });
  }

  /** Saves the user selection to session storage */
  public updateSelection(): void {
    this.statefulService.wrap('ss:oao-accounts-and-services').value = JSON.stringify(this.allProducts);
  }

  /** Validate Page */
  public ValidatePage(): void {
    const isValid = this.checkValidSelection();
    this.enableContinueButton.next({ page: 'retirement', valid: isValid });

    // Set accordian Open status if any required field is active
    const isAnyShareOptOut =
      this.retirementAccount.filter((f) => f.requiredOptInType.toLowerCase() === 'opt-out').length > 0 ? true : false;
    if (!isValid || (isAnyShareOptOut && this.isInitialLoading)) {
      this.expand = true;
    }
  }

  /** Check the Retirement Account Mandatory fields Validation Check */
  public checkValidSelection(): boolean {
    /** Get All Selected retirement savings account */
    const selectedAccount = this.retirementAccount.filter((f) => f.isSelected);
    let isValid = true;
    if (selectedAccount.length > 0) {
      selectedAccount.forEach((element) => {
        const valid = element.taxYear !== '' && element.contributionSource !== '';
        if (!valid) {
          isValid = false;
          return;
        }
      });
    }
    return isValid;
  }

  /**
   * Get all invalid field ids & expand accordion div if any invalid field exists
   * @returns Invalid field ids
   */
  public getInvalidFieldIds(): unknown[] {
    this.ValidatePage();
    const invalidList = [];
    const controls = this.retireAccountForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalidList.push(name);
      }
    }
    return invalidList;
  }

  /** Check has coapplicants */
  public hasCoApplicant(): boolean {
    const membershipType = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').value;
    if (membershipType && membershipType.toString().toLowerCase() === 'business') {
      const responsibleParties = StatefulValue.arraySize('api:oaoMemberInformation/businessInformation/responsibleParties');
      return responsibleParties && responsibleParties > 1;
    }
    return this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean;
  }

  /** Check has optional Services */
  public hasCoApplicantShares(indexParent: number): boolean {
    // Check Is any co-applicant has the same share & its optional params
    const hasCoApplicantShares = [];
    this.coAppProducts.forEach((applicant) => {
      if (applicant.shares[indexParent]) {
        hasCoApplicantShares.push(true);
      }
    });
    // eslint-disable-next-line eqeqeq
    return hasCoApplicantShares.length > 0 && hasCoApplicantShares.filter((f) => f == true).length > 0;
  }

  public isYouthMembership(): boolean {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  }

  /** check Joint Applicant availability. If youth membership, no need to check this condition.*/
  public hasJointApplicants(product: Product): boolean {
    if (this.isYouthMembership()) {
      return true;
    } else {
      return product.numberOfJointApplicants && product.numberOfJointApplicants > 0;
    }
  }
}
